import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { DevHubPaper, DevHubTypography } from '@devxdiscover/devhub-ui';

/**
 * Renders a markdown string as formatted React elements.
 * @param content - Raw markdown string to render.
 */
export const MarkdownMessage = ({ content }: { content: string }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => (
        <DevHubTypography variant="body2" paragraph>
          {children}
        </DevHubTypography>
      ),
      strong: ({ children }) => <strong>{children}</strong>,
      em: ({ children }) => <em>{children}</em>,
      code: ({ inline, children, ...props }: any) =>
        inline ? (
          <code
            style={{
              backgroundColor: 'rgba(0,0,0,0.1)',
              padding: '2px 4px',
              borderRadius: 4,
              fontFamily: 'monospace',
              fontSize: '0.9em',
            }}
            {...props}
          >
            {children}
          </code>
        ) : (
          <pre
            style={{
              backgroundColor: 'rgba(0,0,0,0.1)',
              padding: 8,
              borderRadius: 4,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.9em',
            }}
          >
            <code {...props}>{children}</code>
          </pre>
        ),
      ul: ({ children }) => (
        <ul style={{ margin: '8px 0', paddingLeft: 24 }}>{children}</ul>
      ),
      ol: ({ children }) => (
        <ol style={{ margin: '8px 0', paddingLeft: 24 }}>{children}</ol>
      ),
      li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
      a: ({ children, href }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          {children}
        </a>
      ),
      h1: ({ children }) => (
        <DevHubTypography variant="h5" paragraph>
          {children}
        </DevHubTypography>
      ),
      h2: ({ children }) => (
        <DevHubTypography variant="h6" paragraph>
          {children}
        </DevHubTypography>
      ),
      h3: ({ children }) => (
        <DevHubTypography variant="subtitle1" paragraph>
          {children}
        </DevHubTypography>
      ),
      h4: ({ children }) => (
        <DevHubTypography variant="subtitle2" paragraph>
          {children}
        </DevHubTypography>
      ),
      blockquote: ({ children }) => (
        <div
          style={{
            borderLeft: '4px solid rgba(0,0,0,0.2)',
            paddingLeft: 12,
            margin: '8px 0',
          }}
        >
          {children}
        </div>
      ),
      table: ({ children }) => (
        <DevHubPaper style={{ margin: '12px 0', overflowX: 'auto' }}>
          <Table size="small">{children}</Table>
        </DevHubPaper>
      ),
      thead: ({ children }) => <TableHead>{children}</TableHead>,
      tbody: ({ children }) => <TableBody>{children}</TableBody>,
      tr: ({ children }) => <TableRow hover>{children}</TableRow>,
      th: ({ children }) => (
        <TableCell style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {children}
        </TableCell>
      ),
      td: ({ children }) => <TableCell>{children}</TableCell>,
    }}
  >
    {content}
  </ReactMarkdown>
);
